import { Box, Button, Divider, IconButton, MenuItem, Stack, Tooltip, Typography } from '@vapor/material'
import { Select, Grid, TextField } from '@mui/material'
import { AutocompleteTextField, FormItem } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { CityTaxExonerationCode, CustomerDetailFragment, useCityTaxExonerationsQuery, useCityTaxForPersonFormQuery } from 'src/api/api.generated'
import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { CustomerFormData, FocusedField } from '../customer.form.types'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import * as z from 'zod'
import { Controller } from 'react-hook-form'
import usePersonForm from './person.form.hook'
import { localizedLanguageKeys, Language, languageList } from 'src/api/language'
import { useCallback, useState } from 'react'
import { displayGender, genderList } from 'src/utils/gender'
import AddressInput from 'src/app/components/adress-input/address-input'
import PersonIdentificationDocumentInput from 'src/app/components/identification-document-input/person-identification-document-input'
import TagInput from 'src/app/components/tag-input/tag-input'
import PrivacyConsentCheckbox from 'src/app/components/privacy/privacy-consent-checkbox'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { useProperty, useTenant } from 'src/app/hooks/context/context.hook'
import CountryInput from 'src/app/components/adress-input/country-input'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { HiCIcon } from 'src/app/components/icons/icon'
import FiscalCodeInput from '../components/fiscal-code-input'
import UnsavedChangesDialog from 'src/app/components/unsaved-changes/unsaved-changes-dialog/unsaved-changes-dialog'
import UnsavedChangesTypography from 'src/app/components/unsaved-changes/unsaved-changes-typography/unsaved-changes-typography'
import HicDateRangePicker from 'src/app/components/hic-date-range-picker/hic-date-range-picker'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import HistoricalCustomerResearchGrid from 'src/app/components/form-fields/historical-customer-research/historical-customer-research.grid'
import CustomerTabs from '../components/customer-tabs'
import CompanyLookup from 'src/app/components/customer-lookup/company-lookup'

export type PersonFormProps = {
  appearance: FormLayoutAppearance
  title: string
  loading: boolean
  customer: CustomerDetailFragment | null
  onSubmit: (data: CustomerFormData) => Promise<void>
  onCancel: () => void
  focusedField?: FocusedField
}

export default function PersonForm(props: PersonFormProps) {
  const { appearance, title, loading, customer, onSubmit, onCancel, focusedField } = props
  const showError = useErrorSnackBar()
  const localizeString = useLocalizeString()
  const formatDate = useFormatDate()
  const { t, i18n } = useTranslation()
  const tenant = useTenant()
  const property = useProperty()
  const privacyLog = customer?.privacy?.log
  const [tab, setTab] = useState(0)
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)
  const { data: cityTaxData } = useCityTaxExonerationsQuery({
    variables: {
      tenantId: tenant.id,
      propertyId: property?.id ?? '',
      usage: 'CUSTOMER',
    },
    onError: showError,
  })
  const { data } = useCityTaxForPersonFormQuery({
    variables: {
      filter: {
        tenantId: { eq: tenant.id },
        id: { eq: property?.id },
      },
    },
    onError: showError,
  })
  const seeContractsRange = data?.properties?.[0]?.cityTaxConfiguration?.configurations?.some((configuration) => configuration.rule === 'ROMA' || configuration.rule === 'GENERIC')
  const cityTaxExonerations = cityTaxData?.cityTaxExonerations ?? []

  const { personForm, onFormSubmit, control, errors, formValue, setValue, isDirty } = usePersonForm({ onSubmit, customer })
  type PersonForm = z.infer<typeof personForm>

  const isDialog = appearance.type === 'DIALOG' || appearance.type === 'DRAWER'

  const onRefChange = useCallback(
    (node: HTMLDivElement | null, field: FocusedField) => {
      if (!focusedField) {
        return
      }
      if (node && field === focusedField) {
        node.scrollIntoView()
      }
    },
    [focusedField],
  )

  function onClickOnCancel() {
    if (isDirty) {
      setShowUnsavedChangesDialog(true)
    } else {
      onCancel()
    }
  }

  return (
    <FormLayout
      appearance={
        'onClose' in appearance
          ? {
              ...appearance,
              onClose: onClickOnCancel,
            }
          : appearance
      }
      title={title}
      toolbar={
        <FormLayoutDefaultToolbar
          saveButton={{
            loading: loading,
            disabled: loading,
            clickHandler: onFormSubmit,
          }}
          cancelButton={{
            disabled: loading,
            clickHandler: onClickOnCancel,
            clickConfirmDialog: (
              <UnsavedChangesDialog
                open={showUnsavedChangesDialog}
                handleCloseDialog={() => {
                  setShowUnsavedChangesDialog(false)
                }}
                onCloseAndDiscard={onCancel}
              />
            ),
          }}
          leftItem={<UnsavedChangesTypography show={isDirty} />}
        />
      }
    >
      <form onSubmit={onFormSubmit}>
        <Grid container spacing={2}>
          {customer ? (
            <Grid item xs={12}>
              <CustomerTabs value={tab} onChange={setTab} disabled={loading} />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="titleSmall">{t('pms.common.customer.generic_data_subtitle')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
            </>
          )}
          {customer && tab === 1 ? (
            <>
              <Grid item xs={12} width={800}>
                <HistoricalCustomerResearchGrid propertyId={property?.id ?? ''} tenantId={tenant.id} initialCustomer={customer} hideCustomerFilter />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={6}>
                <Controller<PersonForm, 'lastName'>
                  name={'lastName'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={`${t('pms.common.customer.lastName')}*`}
                      errorMessage={errors.lastName?.message}
                    >
                      <TextField //
                        disabled={loading}
                        fullWidth
                        value={field.value || ''}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                        }}
                        error={errors.lastName ? true : false}
                        inputRef={field.ref}
                        size={'small'}
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller<PersonForm, 'firstName'>
                  name={'firstName'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={`${t('pms.common.customer.firstName')}*`}
                      errorMessage={errors.firstName?.message}
                    >
                      <TextField //
                        fullWidth
                        disabled={loading}
                        value={field.value || ''}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                        }}
                        error={errors.firstName ? true : false}
                        inputRef={field.ref}
                        size={'small'}
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                lg={isDialog ? 6 : 4}
                ref={(node) => {
                  onRefChange(node, 'EMAIL')
                }}
              >
                <Controller<PersonForm, 'mailContact'>
                  name={'mailContact'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.mail')}
                      errorMessage={errors.mailContact?.message}
                    >
                      <TextField //
                        fullWidth
                        disabled={loading}
                        value={field.value || ''}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                        }}
                        error={errors?.mailContact ? true : false}
                        inputRef={field.ref}
                        size={'small'}
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={isDialog ? 6 : 3}>
                <Controller<PersonForm, 'phoneContact'>
                  name={'phoneContact'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.phone')}
                      errorMessage={errors.phoneContact?.message}
                    >
                      <TextField //
                        fullWidth
                        disabled={loading}
                        value={field.value || ''}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                        }}
                        error={errors?.phoneContact ? true : false}
                        inputRef={field.ref}
                        size={'small'}
                      />
                    </FormItem>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={isDialog ? 6 : 3}>
                <Controller<PersonForm, 'language'>
                  name={'language'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={`${t('pms.common.customer.contact_language')}*`}
                      errorMessage={errors.language?.message}
                    >
                      <AutocompleteTextField<Language> //
                        getOptionLabel={(option: Language) => `${languageList[option].nativeName} (${languageList[option].name})`}
                        options={[...localizedLanguageKeys]}
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        error={errors.language ? true : false}
                        disabled={loading}
                        inputRef={field.ref}
                        size="small"
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={isDialog ? 6 : 2}>
                <Controller<PersonForm, 'gender'>
                  name={'gender'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.gender')}
                      errorMessage={errors.gender?.message}
                    >
                      <Select //
                        error={errors.gender ? true : false}
                        disabled={loading}
                        value={field.value || ''}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                        }}
                        size={'small'}
                      >
                        {genderList.map((gender) => (
                          <MenuItem key={gender} value={gender}>
                            {displayGender(t, gender)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={isDialog ? 6 : 2}>
                <Controller<PersonForm, 'relatedCompanyId'>
                  name={'relatedCompanyId'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.related_company')}
                      errorMessage={errors.relatedCompanyId?.message}
                    >
                      <CompanyLookup
                        onChange={(company) => {
                          if (company?.id !== field.value) field.onChange(company?.id ?? null)
                        }}
                        value={field.value ? { id: field.value } : null}
                        error={Boolean(errors.relatedCompanyId?.message)}
                        disabled={loading}
                        placeholder=""
                        size="small"
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="titleSmall">{t('pms.common.customer.address_subtitle')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
              <Grid //
                item
                xs={12}
                ref={(node) => {
                  onRefChange(node, 'ADDRESS')
                }}
              >
                <Controller<PersonForm, 'address'> //
                  name={'address'}
                  control={control}
                  render={({ field }) => (
                    <AddressInput //
                      disabled={loading}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)
                      }}
                      fieldsSmall
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="titleSmall">{t('pms.common.customer.identification_sub_title')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller<PersonForm, 'birthDate'> //
                  name={'birthDate'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.birth_date')}
                      errorMessage={errors.birthDate?.message}
                    >
                      <DatePicker //
                        disabled={loading}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        value={field.value || null}
                        slotProps={{
                          textField: {
                            error: errors.birthDate ? true : false,
                            size: 'small',
                          },
                        }}
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller<PersonForm, 'birthAddress'> //
                  name={'birthAddress'}
                  control={control}
                  render={({ field }) => (
                    <AddressInput //
                      disabled={loading}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)
                      }}
                      cityLabel={t('pms.common.customer.birth_city')}
                      addressLabel={t('pms.common.customer.birth_address')}
                      countryLabel={t('pms.common.customer.birth_country')}
                      italianCityLabel={t('pms.common.customer.birth_italian_city')}
                      municipalityLabel={t('pms.common.customer.birth_municipality')}
                      hideAddress={true}
                      fieldsSmall
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller<PersonForm, 'nationalityId'> //
                  name={'nationalityId'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.nationality')}
                      errorMessage={errors.nationalityId?.message}
                    >
                      <CountryInput //
                        value={field.value ? { id: field.value } : null}
                        onChange={(country) => {
                          field.onChange(country?.id ?? null)
                        }}
                        error={errors.nationalityId ? true : false}
                        disabled={loading}
                        size={'small'}
                      />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid //
                item
                xs={12}
                md={6}
                ref={(node) => onRefChange(node, 'FISCAL_CODE')}
              >
                <Controller<PersonForm, 'fiscalCode'>
                  name={'fiscalCode'}
                  control={control}
                  render={({ field }) => (
                    <FiscalCodeInput
                      value={field.value || ''}
                      onChange={(value) => {
                        field.onChange(value)
                      }}
                      errorMessage={errors.fiscalCode?.message}
                      disabled={loading}
                      customer={formValue}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller<PersonForm, 'identificationDocument'> //
                  name={'identificationDocument'}
                  control={control}
                  render={({ field }) => (
                    <PersonIdentificationDocumentInput //
                      disabled={loading}
                      value={field.value || {}}
                      onChange={(value) => {
                        field.onChange(value || undefined)
                      }}
                      error={errors.identificationDocument}
                      fieldsSmall
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <span>
                  <Typography variant="titleSmall">{t('pms.common.customer.city_tax')}</Typography>
                </span>
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
              <Grid item xs={6}>
                <Controller<PersonForm, 'exoneration'>
                  name={'exoneration'}
                  control={control}
                  render={({ field }) => (
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.common.customer.exoneration')}
                      errorMessage={errors.exoneration?.message}
                    >
                      <Select //
                        error={errors.exoneration ? true : false}
                        disabled={loading}
                        value={field.value ?? 'default'}
                        onChange={(event) => {
                          field.onChange(event.target.value !== 'default' ? (event.target.value as CityTaxExonerationCode) : null)
                        }}
                        size={'small'}
                      >
                        <MenuItem value={'default'}>{t('pms.common.customer.exoneration.default')}</MenuItem>
                        {cityTaxExonerations.map((cityTaxExoneration) => {
                          return (
                            <MenuItem key={cityTaxExoneration.code} value={cityTaxExoneration.code}>
                              {cityTaxExoneration.code} - {localizeString(cityTaxExoneration.description)}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </FormItem>
                  )}
                />
              </Grid>
              {seeContractsRange && (
                <Grid item xs={12}>
                  <Controller<PersonForm, `contracts`>
                    name={`contracts`}
                    control={control}
                    render={({ field: { value: contracts, onChange } }) => (
                      <FormItem fullWidth fieldLabel={t('pms.common.customer.contracts_label')}>
                        <>
                          {(contracts ?? []).map((contract, index) => (
                            <Box key={index} marginBottom={1} display={'flex'} alignItems={'end'} gap={1}>
                              <HicDateRangePicker
                                value={[contract?.start ?? null, contract?.end ?? null]}
                                disabled={loading}
                                end={{
                                  endPlaceholder: t('commons.datepicker.placeholeder'),
                                  endDateError: Array.isArray(errors?.contracts) ? errors?.contracts?.[index]?.end?.message : undefined,
                                  endLabel: t('pms.settings.citytax_configuration.amount_by_period.to'),
                                }}
                                start={{
                                  startPlaceholder: t('commons.datepicker.placeholeder'),
                                  startDateError: Array.isArray(errors?.contracts) ? errors?.contracts?.[index]?.start?.message : undefined,
                                  startLabel: t('pms.settings.citytax_configuration.amount_by_period.from'),
                                }}
                                onChange={(dates) => {
                                  if (dates[0] && dates[1]) {
                                    onChange(
                                      contracts?.map((contract, i) => {
                                        if (i === index) {
                                          return {
                                            start: dates[0],
                                            end: dates[1],
                                          }
                                        }
                                        return contract
                                      }),
                                    )
                                  }
                                }}
                                fieldSmall
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  onChange((contracts ?? []).filter((_, i) => i !== index))
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </IconButton>
                            </Box>
                          ))}
                        </>
                      </FormItem>
                    )}
                  />
                  <Button
                    size="small"
                    id="customer-form-add-contract"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => {
                      setValue('contracts', [...(formValue.contracts ?? []), { start: null, end: null }])
                    }}
                  >
                    {t('pms.common.customer.contracts')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12}>
                <span>
                  <Typography variant="titleSmall">{t('pms.common.customer.tag')}</Typography>
                </span>
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
              <Grid item xs={12}>
                <Controller<PersonForm, 'tagsId'> //
                  name={'tagsId'}
                  control={control}
                  render={({ field }) => (
                    <TagInput //
                      value={field.value || []}
                      onChange={(value) => {
                        field.onChange(value)
                      }}
                      size={'small'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} display={'flex'} alignItems={'center'} columnGap={0.5}>
                <Typography variant="titleSmall">{t('pms.common.customer.privacy_subtitle')}</Typography>
                {privacyLog && (
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {t('pms.common.customer.privacy_subtitle_tooltip', {
                          timestamp: privacyLog?.timestamp ? formatDate(privacyLog?.timestamp, 'Pp') : '',
                          device: privacyLog.platform ?? '',
                          ip: privacyLog.ip ?? '',
                          browser: privacyLog.userAgent ?? '',
                        })}
                      </span>
                    }
                  >
                    <span>
                      <HiCIcon>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </HiCIcon>
                    </span>
                  </Tooltip>
                )}
              </Grid>
              <Grid item xs={12}>
                <Divider light />
              </Grid>
              <Grid item xs={12} md={isDialog ? 12 : 4}>
                <Stack>
                  <Controller<PersonForm, 'privacyManagement'> //
                    name={'privacyManagement'}
                    control={control}
                    render={({ field }) => (
                      <PrivacyConsentCheckbox //
                        value={field.value || { consent: false }}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        label={t('pms.common.customer.privacy_management')}
                        i18n={i18n}
                        disabled={loading}
                      />
                    )}
                  />
                  <Controller<PersonForm, 'privacyMarketing'> //
                    name={'privacyMarketing'}
                    control={control}
                    render={({ field }) => (
                      <PrivacyConsentCheckbox //
                        value={field.value || { consent: false }}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        label={t('pms.common.customer.privacy_marketing')}
                        i18n={i18n}
                        disabled={loading}
                      />
                    )}
                  />
                  <Controller<PersonForm, 'privacyDataThirdParty'> //
                    name={'privacyDataThirdParty'}
                    control={control}
                    render={({ field }) => (
                      <PrivacyConsentCheckbox //
                        value={field.value || { consent: false }}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        label={t('pms.common.customer.privacy_data_third_party')}
                        i18n={i18n}
                        disabled={loading}
                      />
                    )}
                  />
                  <Controller<PersonForm, 'privacyProfiledMarketing'> //
                    name={'privacyProfiledMarketing'}
                    control={control}
                    render={({ field }) => (
                      <PrivacyConsentCheckbox //
                        value={field.value || { consent: false }}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        label={t('pms.common.customer.privacy_profiled_marketing')}
                        i18n={i18n}
                        disabled={loading}
                      />
                    )}
                  />
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </form>
    </FormLayout>
  )
}
